/**
 * First-party RUM beacon.
 *
 * Events go to our own collector on analytics.logpulse.io (workers/rum), which
 * forwards them into the `rum` index of our own LogPulse tenant. No third-party
 * analytics script, no ingest key in the browser.
 *
 * What is collected: pageviews, campaign attribution (utm_*), custom events,
 * link clicks, engagement (scroll depth + time on page) and web vitals.
 *
 * What is deliberately NOT collected, because it is what would force a consent
 * banner back onto the site:
 *   - nothing is stored on the device: the session id and the campaign
 *     attribution live in module variables for the lifetime of the page load;
 *   - no raw IP (the collector hashes it, daily-rotating and salted);
 *   - no full query strings, no precise viewport, no user-agent version — the
 *     combination of those is what makes a browser fingerprint.
 * Adding any of them back is a privacy decision, not a technical one.
 */

import { decorateLocaleUrl, decorateSignupUrl, isAppHost, isMarketingHost } from './signup-attribution';

const ENDPOINT = import.meta.env.VITE_RUM_ENDPOINT || 'https://analytics.logpulse.io/e';

type EventKind = 'pageview' | 'event' | 'click' | 'pageleave' | 'vitals';

type Props = Record<string, string | number | boolean>;

interface RumEvent {
  kind: EventKind;
  name?: string;
  path?: string;
  referrer?: string;
  locale?: string;
  session?: string;
  utm?: Record<string, string>;
  props?: Props;
}

/** Random enough to group one visit; never persisted, never sent anywhere else. */
const SESSION_ID = Math.random().toString(36).slice(2, 14);

const FLUSH_DELAY_MS = 1_000;

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;

let queue: RumEvent[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let listenersAttached = false;

/**
 * Campaign attribution is read once, from the URL the visitor landed on, and
 * then rides along on every event of the session. Read per event instead and a
 * campaign would only ever be credited for the landing page, never for the
 * signup click three pages later.
 */
let attribution: Record<string, string> = {};
let attributionCaptured = false;
/** Where the visit began, for signup attribution (see signup-attribution.ts). */
let landingPath = '';
let referrerHost = '';

/* ---- transport ---- */

const EXPLICIT_ENDPOINT = Boolean(import.meta.env.VITE_RUM_ENDPOINT);

/**
 * Off on localhost and in dev builds. The prerender step drives a real browser
 * against a local static server, so without this every release would ship a
 * burst of fake pageviews. Setting VITE_RUM_ENDPOINT overrides it: pointing the
 * beacon somewhere by hand is only ever deliberate.
 */
function enabled(): boolean {
  if (typeof window === 'undefined') return false;
  if (EXPLICIT_ENDPOINT) return true;
  if (!import.meta.env.PROD) return false;
  const { hostname } = window.location;
  return hostname !== 'localhost' && hostname !== '127.0.0.1' && !hostname.endsWith('.local');
}

function send(events: RumEvent[]): void {
  if (events.length === 0) return;
  const body = JSON.stringify({ events });

  // text/plain keeps this a CORS "simple request", so the beacon never pays for
  // a preflight round trip. The collector parses the JSON itself.
  if (typeof navigator.sendBeacon === 'function') {
    const blob = new Blob([body], { type: 'text/plain;charset=UTF-8' });
    if (navigator.sendBeacon(ENDPOINT, blob)) return;
  }

  void fetch(ENDPOINT, {
    method: 'POST',
    body,
    headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
    keepalive: true,
    mode: 'cors',
  }).catch(() => {
    /* analytics must never break the page */
  });
}

function flush(): void {
  if (flushTimer !== null) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (queue.length === 0) return;
  const batch = queue;
  queue = [];
  send(batch);
}

function scheduleFlush(): void {
  if (flushTimer !== null) return;
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

function enqueue(event: RumEvent): void {
  if (!enabled()) return;
  attachListeners();
  queue.push({
    ...event,
    session: SESSION_ID,
    ...(Object.keys(attribution).length > 0 ? { utm: attribution } : {}),
  });
  if (queue.length >= 10) {
    flush();
    return;
  }
  scheduleFlush();
}

/* ---- attribution ---- */

function captureAttribution(): void {
  if (attributionCaptured || typeof window === 'undefined') return;
  attributionCaptured = true;
  landingPath = window.location.pathname;
  try {
    referrerHost = document.referrer ? new URL(document.referrer).host : '';
  } catch {
    referrerHost = '';
  }
  const params = new URLSearchParams(window.location.search);
  const hadCampaign = UTM_KEYS.some((key) => params.has(key));
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) attribution[key] = value.slice(0, 120);
  }
  // Common ad-click ids, so paid traffic is still attributable when the
  // campaign forgot the utm_* set.
  for (const key of ['gclid', 'fbclid', 'msclkid']) {
    if (params.get(key)) {
      attribution.utm_source = attribution.utm_source || key.replace('clid', '');
      attribution.utm_medium = attribution.utm_medium || 'paid';
      break;
    }
  }
  // OpenAI adds its own click id (`?oppref=`) to every ChatGPT ad link. We only
  // use its presence, as a fallback campaign label; the id itself is never
  // kept, sent to the collector or passed on to the app (ADR-0162).
  if (params.get('oppref')) {
    attribution.utm_source = attribution.utm_source || 'chatgpt';
    attribution.utm_medium = attribution.utm_medium || 'paid';
  }

  // Drop the campaign parameters from the address bar once they are captured.
  // Cosmetic at first glance, but it is what keeps attribution alive: a click
  // on an in-page anchor like "/#pricing" from "/?utm_source=..." changes more
  // than the fragment, so the browser does a FULL page load and this module
  // (with the campaign in it) is thrown away. Clean the query first and the
  // same click stays a same-document navigation.
  if ((hadCampaign || Object.keys(attribution).length > 0) && window.history?.replaceState) {
    // oppref too: left behind, it keeps the query non-empty and an anchor
    // click becomes the full reload this is here to prevent.
    for (const key of [...UTM_KEYS, 'gclid', 'fbclid', 'msclkid', 'oppref']) params.delete(key);
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      '',
      `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`,
    );
  }
}

/**
 * The campaign labels of this visit (utm_*), for pages that adapt their copy
 * to the ad that was clicked. Captures first if nothing has yet, because the
 * capture strips the labels from the address bar.
 */
export function getCampaign(): Readonly<Record<string, string>> {
  captureAttribution();
  return attribution;
}

/* ---- engagement ---- */

let pageStart = Date.now();
let maxScroll = 0;
let currentPath = '';
/** pagehide and visibilitychange both fire on teardown; one leave per page. */
let leaveSent = false;

function trackScroll(): void {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  const percent = scrollable > 0 ? Math.round((window.scrollY / scrollable) * 100) : 100;
  if (percent > maxScroll) maxScroll = Math.min(100, percent);
}

/** Emitted when the visitor leaves a page, by navigation or by closing it. */
function sendPageleave(): void {
  if (!currentPath || leaveSent) return;
  leaveSent = true;
  enqueue({
    kind: 'pageleave',
    path: currentPath,
    props: {
      seconds: Math.round((Date.now() - pageStart) / 1000),
      scroll_pct: maxScroll,
    },
  });
}

/* ---- click tracking ---- */

/** Text of the clicked element, trimmed to something loggable. */
function labelOf(element: Element): string {
  const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
  return text.slice(0, 80);
}

function handleClick(rawEvent: MouseEvent): void {
  const target = rawEvent.target;
  if (!(target instanceof Element)) return;

  const element = target.closest('a[href], [data-analytics]');
  if (!element) return;

  const analyticsId = element.getAttribute('data-analytics') || undefined;
  const href = element.getAttribute('href');

  if (!href) {
    // A tagged non-link element (button, card) — the tag is the whole point.
    if (analyticsId) {
      enqueue({
        kind: 'click',
        name: analyticsId,
        path: window.location.pathname,
        props: { label: labelOf(element) },
      });
      flush();
    }
    return;
  }

  let url: URL;
  try {
    url = new URL(href, window.location.href);
  } catch {
    return;
  }

  const outbound = url.host !== window.location.host;
  // A click through to the app carries the visit's campaign along, so the
  // signup can be credited to it. Rewritten before the browser follows it.
  if (outbound && isAppHost(url.host) && element instanceof HTMLAnchorElement) {
    element.href = decorateSignupUrl(url, {
      utm: attribution,
      site: window.location.host,
      landing: landingPath || window.location.pathname,
      referrerHost,
    }).toString();
  } else if (outbound && isMarketingHost(url.host) && element instanceof HTMLAnchorElement && Object.keys(attribution).length > 0) {
    // A language switch is a full load on another domain: without this the
    // campaign is lost there and the signup counts as organic.
    element.href = decorateLocaleUrl(url, attribution).toString();
  }
  enqueue({
    kind: 'click',
    name: analyticsId,
    path: window.location.pathname,
    props: {
      // Host and path only: a target URL's query string is as personal as our
      // own, and campaign ids on outbound links add nothing we don't have.
      target_host: url.host,
      target_path: `${url.pathname}${url.hash}`.slice(0, 200),
      outbound,
      label: labelOf(element),
      ...(url.protocol === 'mailto:' || url.protocol === 'tel:'
        ? { contact: url.protocol.replace(':', '') }
        : {}),
    },
  });
  // A click is usually the last thing that happens on this page: send it now
  // rather than hoping the unload leaves time for the debounced flush.
  flush();
}

/* ---- listeners ---- */

function attachListeners(): void {
  if (listenersAttached || typeof document === 'undefined') return;
  listenersAttached = true;

  // pagehide covers back/forward cache; visibilitychange covers tab switches
  // and mobile app switches, where pagehide is not guaranteed to fire.
  const leave = () => {
    sendPageleave();
    flush();
  };
  window.addEventListener('pagehide', leave);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') leave();
  });

  // Capture phase: a click handler that stops propagation shouldn't hide the
  // click from us. Passive: this must never delay the interaction.
  document.addEventListener('click', handleClick, { capture: true, passive: true });
  window.addEventListener('scroll', trackScroll, { passive: true });
}

/* ---- public API ---- */

/** Custom event, e.g. trackEvent('plan_wizard_completed', { plan: 'business' }). */
export function trackEvent(name: string, props?: Props): void {
  // The page an event fired on is half its meaning: "wizard completed" on
  // /pricing and on the homepage are different signals.
  enqueue({
    kind: 'event',
    name,
    props,
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
}

/** One per page load and per SPA route change. */
export function trackPageview(path: string, locale?: string): void {
  if (typeof window === 'undefined') return;
  captureAttribution();

  // A route change ends the previous page: report how far it got before the
  // counters reset.
  if (currentPath && currentPath !== path) sendPageleave();

  currentPath = path;
  pageStart = Date.now();
  maxScroll = 0;
  leaveSent = false;

  const connection = (
    navigator as Navigator & { connection?: { effectiveType?: string } }
  ).connection;

  enqueue({
    kind: 'pageview',
    path,
    locale,
    referrer: document.referrer,
    props: {
      // Rounded to 50px: enough to tell phone from laptop from ultrawide,
      // coarse enough not to become a fingerprint.
      viewport_w: Math.round(window.innerWidth / 50) * 50,
      viewport_h: Math.round(window.innerHeight / 50) * 50,
      language: (navigator.language || '').slice(0, 12),
      ...(connection?.effectiveType ? { connection: connection.effectiveType } : {}),
    },
  });
}

/* ---- Web vitals ----------------------------------------------------------
 * Hand-rolled rather than pulled from a library: these are the ones we would
 * act on, and they are reachable from PerformanceObserver without adding a
 * dependency to the marketing bundle. INP is deliberately absent — measuring it
 * correctly is fiddly enough that a library is the honest way to add it.
 * -------------------------------------------------------------------------- */

interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
}

let vitalsStarted = false;

export function trackWebVitals(): void {
  if (vitalsStarted || !enabled() || typeof PerformanceObserver === 'undefined') return;
  vitalsStarted = true;

  let lcp = 0;
  let cls = 0;

  const observe = (type: string, callback: (entries: PerformanceEntryList) => void) => {
    try {
      const observer = new PerformanceObserver((list) => callback(list.getEntries()));
      observer.observe({ type, buffered: true });
    } catch {
      /* unsupported entry type in this browser */
    }
  };

  // The last LCP candidate before the page is hidden is the real one.
  observe('largest-contentful-paint', (entries) => {
    const last = entries[entries.length - 1];
    if (last) lcp = last.startTime;
  });

  // Shifts that follow user input are expected, so they don't count.
  observe('layout-shift', (entries) => {
    for (const entry of entries) {
      const shift = entry as LayoutShift;
      if (!shift.hadRecentInput) cls += shift.value;
    }
  });

  const report = () => {
    const navigation = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    const fcp = performance.getEntriesByName('first-contentful-paint')[0];

    enqueue({
      kind: 'vitals',
      path: window.location.pathname,
      props: {
        lcp_ms: Math.round(lcp),
        cls: Number(cls.toFixed(3)),
        fcp_ms: fcp ? Math.round(fcp.startTime) : 0,
        ttfb_ms: navigation ? Math.round(navigation.responseStart) : 0,
        load_ms: navigation ? Math.round(navigation.duration) : 0,
      },
    });
    flush();
  };

  // Report once, when the page goes away — that is the only moment LCP and CLS
  // are final.
  let reported = false;
  const reportOnce = () => {
    if (reported) return;
    reported = true;
    report();
  };
  window.addEventListener('pagehide', reportOnce);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') reportOnce();
  });
}
